import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getTransferDetails, escalateTransfer, respondToTransfer } from "../../../../lib/api/claim.api";
import type { TransferDetails, TransferStatus } from "../../../../lib/types/claim.types";
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ArrowUpRight,
  RefreshCw,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import { useAuth } from "../../../../Context/AuthContext";

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  TransferStatus,
  {
    label: string;
    description: string;
    icon: React.ReactNode;
    color: string;
    bgColor: string;
    borderColor: string;
  }
> = {
  pending_owner_response: {
    label: "Awaiting owner response",
    description: "The current owner has been notified and has 7 days to respond.",
    icon: <Clock className="w-5 h-5" />,
    color: "text-amber-700",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
  },
  owner_approved_pending_admin: {
    label: "Owner approved",
    description: "The owner approved your request. An admin is now reviewing it.",
    icon: <ShieldCheck className="w-5 h-5" />,
    color: "text-blue-700",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
  },
  owner_rejected: {
    label: "Rejected by owner",
    description:
      "The current owner declined your transfer request. You may escalate to admin review.",
    icon: <XCircle className="w-5 h-5" />,
    color: "text-red-700",
    bgColor: "bg-red-50",
    borderColor: "border-red-200",
  },
  escalated_pending_admin: {
    label: "Under admin review",
    description:
      "Your transfer request has been escalated and is being reviewed by our team.",
    icon: <ArrowUpRight className="w-5 h-5" />,
    color: "text-purple-700",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
  },
  approved: {
    label: "Transfer approved",
    description: "Ownership has been successfully transferred to you.",
    icon: <CheckCircle className="w-5 h-5" />,
    color: "text-emerald-700",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-200",
  },
  rejected: {
    label: "Transfer rejected",
    description:
      "Your transfer request was not approved. Please contact support if you believe this is an error.",
    icon: <XCircle className="w-5 h-5" />,
    color: "text-red-700",
    bgColor: "bg-red-50",
    borderColor: "border-red-200",
  },
  disputed: {
    label: "Under dispute review",
    description: "Ownership is being disputed. An admin will resolve this.",
    icon: <AlertTriangle className="w-5 h-5" />,
    color: "text-orange-700",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
  },
};

// ─── Progress bar steps ───────────────────────────────────────────────────────

const STEPS: { key: TransferStatus[]; label: string }[] = [
  { key: ["pending_owner_response"], label: "Submitted" },
  {
    key: ["owner_approved_pending_admin", "owner_rejected", "escalated_pending_admin"],
    label: "Owner response",
  },
  { key: ["escalated_pending_admin", "owner_approved_pending_admin"], label: "Admin review" },
  { key: ["approved", "rejected", "disputed"], label: "Decision" },
];

function getStepIndex(status: TransferStatus): number {
  if (status === "pending_owner_response") return 0;
  if (status === "owner_rejected") return 1;
  if (status === "owner_approved_pending_admin" || status === "escalated_pending_admin") return 2;
  return 3;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function TransferStatusPage() {
  const { transferId } = useParams<{ transferId: string }>();

  const [transfer, setTransfer] = useState<TransferDetails | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [escalating, setEscalating] = useState(false);
  const [escalateError, setEscalateError] = useState<string | null>(null);
  const [escalateReason, setEscalateReason] = useState("");
  const [showEscalateForm, setShowEscalateForm] = useState(false);

  // Owner respond state
  const [responding, setResponding] = useState(false);
  const [respondError, setRespondError] = useState<string | null>(null);
  const [respondNotes, setRespondNotes] = useState("");

  const { accessToken, user } = useAuth();

  const fetchTransfer = async () => {
    setLoading(true);
    setLoadError(null);
    if (!transferId) return;
    try {
      const data = await getTransferDetails(transferId, accessToken);
      setTransfer(data);
    } catch (err: any) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTransfer(); }, [transferId]);

  const handleEscalate = async () => {
    if (!transferId || escalating) return;
    setEscalating(true);
    setEscalateError(null);
    try {
      await escalateTransfer(transferId, { escalationReason: escalateReason }, accessToken);
      await fetchTransfer();
      setShowEscalateForm(false);
    } catch (err: any) {
      setEscalateError(err.message);
    } finally {
      setEscalating(false);
    }
  };

  const handleRespond = async (decision: "approve" | "reject") => {
    if (!transferId || responding) return;
    setResponding(true);
    setRespondError(null);
    try {
      await respondToTransfer(transferId, { decision, notes: respondNotes }, accessToken);
      await fetchTransfer();
    } catch (err: any) {
      setRespondError(err.message);
    } finally {
      setResponding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center font-['DM_Sans',sans-serif]">
        <Loader2 className="w-8 h-8 text-[#C17B3F] animate-spin" />
      </div>
    );
  }

  if (loadError || !transfer) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center font-['DM_Sans',sans-serif]">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <p className="text-[#1C1917] font-semibold">Failed to load transfer</p>
          <p className="text-sm text-[#6B6460] mt-1">{loadError || "Transfer not found."}</p>
          <button
            onClick={fetchTransfer}
            className="mt-4 flex items-center gap-2 text-sm text-[#C17B3F] hover:underline mx-auto"
          >
            <RefreshCw className="w-4 h-4" /> Try again
          </button>
        </div>
      </div>
    );
  }

  const config = STATUS_CONFIG[transfer.status];
  const stepIndex = getStepIndex(transfer.status);

  const dueDate = transfer.ownerResponseDueAt
    ? new Date(transfer.ownerResponseDueAt).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <div className="min-h-screen bg-[#FAF9F7] font-['DM_Sans',sans-serif]">
      <div className="border-b border-[#E8E4DC] bg-white px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ArrowUpRight className="w-5 h-5 text-[#C17B3F]" />
            <span className="text-sm font-medium text-[#6B6460] tracking-wide uppercase">
              Transfer Status
            </span>
          </div>
          <button
            onClick={fetchTransfer}
            className="flex items-center gap-1.5 text-xs text-[#9B918B] hover:text-[#C17B3F] transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Restaurant name */}
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#9B918B] mb-1">
            Restaurant
          </p>
          <h1
            className="text-[2rem] font-semibold text-[#1C1917]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {transfer.restaurantId?.name ?? "Restaurant"}
          </h1>
        </div>

        {/* Progress tracker */}
        <div className="flex items-center gap-0 mb-10">
          {STEPS.map((step, i) => {
            const isComplete = i < stepIndex;
            const isActive = i === stepIndex;
            return (
              <React.Fragment key={i}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                      isComplete
                        ? "bg-[#C17B3F] border-[#C17B3F] text-white"
                        : isActive
                        ? "bg-white border-[#C17B3F] text-[#C17B3F]"
                        : "bg-white border-[#E8E4DC] text-[#C4B9AD]"
                    }`}
                  >
                    {isComplete ? "✓" : i + 1}
                  </div>
                  <span
                    className={`text-xs mt-1.5 text-center leading-tight ${
                      isActive ? "text-[#C17B3F] font-semibold" : "text-[#9B918B]"
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div
                    className={`h-0.5 flex-shrink-0 w-10 mt-[-14px] ${
                      i < stepIndex ? "bg-[#C17B3F]" : "bg-[#E8E4DC]"
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Status card */}
        <div
          className={`${config?.bgColor} ${config?.borderColor} border-2 rounded-2xl px-6 py-5 mb-8 flex items-start gap-4`}
        >
          <div className={`${config?.color} flex-shrink-0 mt-0.5`}>{config.icon}</div>
          <div>
            <p className={`font-semibold ${config?.color} text-sm`}>{config?.label}</p>
            <p className="text-sm text-[#44403C] mt-1 leading-relaxed">{config?.description}</p>
            {transfer.status === "pending_owner_response" && dueDate && (
              <p className="text-xs text-[#6B6460] mt-2">
                Owner must respond by{" "}
                <strong className="text-[#1C1917]">{dueDate}</strong>
              </p>
            )}
          </div>
        </div>

        {/* Transfer details */}
        <div className="bg-white border border-[#E8E4DC] rounded-2xl divide-y divide-[#F5F0EB] mb-8">
          {[
            { label: "Your role", value: transfer.requesterRole },
            { label: "Reason submitted", value: transfer.reason },
            transfer.ownerNotes && { label: "Owner's notes", value: transfer.ownerNotes },
            transfer.adminNotes && { label: "Admin notes", value: transfer.adminNotes },
            {
              label: "Submitted on",
              value: new Date(transfer.createdAt).toLocaleDateString("en-US", {
                month: "long", day: "numeric", year: "numeric",
              }),
            },
          ]
            .filter(Boolean)
            .map((item: any, i) => (
              <div key={i} className="px-6 py-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-[#9B918B] mb-1">
                  {item.label}
                </p>
                <p className="text-sm text-[#1C1917] leading-relaxed">{item.value}</p>
              </div>
            ))}
        </div>

        {/* ── ESCALATE (owner_rejected only) ─────────────────────────────── */}
        {transfer.status === "owner_rejected" && (
          <div className="bg-white border border-[#E8E4DC] rounded-2xl p-6">
            <h3 className="font-semibold text-[#1C1917] text-sm mb-2">
              Escalate to admin review
            </h3>
            <p className="text-sm text-[#6B6460] mb-4 leading-relaxed">
              If you believe the owner's rejection was unjustified, you can escalate
              this request for a neutral admin review.
            </p>

            {!showEscalateForm ? (
              <button
                onClick={() => setShowEscalateForm(true)}
                className="flex items-center gap-2 px-5 py-3 bg-[#FDF5EC] border border-[#F0DFC5] rounded-xl text-sm font-semibold text-[#C17B3F] hover:bg-[#F8EDE0] transition-colors"
              >
                <ArrowUpRight className="w-4 h-4" />
                Escalate request
              </button>
            ) : (
              <div>
                <textarea
                  value={escalateReason}
                  onChange={(e) => setEscalateReason(e.target.value)}
                  placeholder="Explain why you're escalating this request (optional)…"
                  rows={3}
                  className="w-full px-4 py-3 bg-[#FAF9F7] border-2 border-[#E8E4DC] rounded-xl text-sm text-[#1C1917] placeholder-[#C4B9AD] resize-none outline-none focus:border-[#C17B3F] focus:ring-2 focus:ring-[#C17B3F]/15 transition-all mb-3"
                />
                {escalateError && (
                  <p className="text-sm text-red-600 mb-3">{escalateError}</p>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={handleEscalate}
                    disabled={escalating}
                    className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-[#C17B3F] hover:bg-[#A8652F] text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-60"
                  >
                    {escalating ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Confirm escalation"
                    )}
                  </button>
                  <button
                    onClick={() => setShowEscalateForm(false)}
                    className="px-5 py-3 border border-[#E8E4DC] rounded-xl text-sm font-medium text-[#6B6460] hover:bg-[#F5F0EB] transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── OWNER RESPOND PANEL ────────────────────────────────────────── */}
        {/* NOTE: This block should only render if currentUser.uid === restaurant.ownerId.
            Pass `isOwner` as a prop or from your auth context. */}
        {user && user.id === transfer.restaurantId.ownerId && transfer.status === "pending_owner_response" && (
          <OwnerRespondPanel
            responding={responding}
            respondNotes={respondNotes}
            setRespondNotes={setRespondNotes}
            respondError={respondError}
            onRespond={handleRespond}
          />
        )}
      </div>
    </div>
  );
}

// ─── Owner respond sub-component ──────────────────────────────────────────────

function OwnerRespondPanel({
  responding,
  respondNotes,
  setRespondNotes,
  respondError,
  onRespond,
}: {
  responding: boolean;
  respondNotes: string;
  setRespondNotes: (v: string) => void;
  respondError: string | null;
  onRespond: (decision: "approve" | "reject") => void;
}) {
  return (
    <div className="bg-white border border-[#E8E4DC] rounded-2xl p-6 mt-6">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
        <h3 className="font-semibold text-[#1C1917] text-sm">
          Action required — you are the current owner
        </h3>
      </div>
      <p className="text-sm text-[#6B6460] mb-5 leading-relaxed">
        Someone has requested to take ownership of this restaurant. Review their
        request and choose to approve or reject it.
      </p>
      <textarea
        value={respondNotes}
        onChange={(e) => setRespondNotes(e.target.value)}
        placeholder="Add a note (optional)…"
        rows={3}
        className="w-full px-4 py-3 bg-[#FAF9F7] border-2 border-[#E8E4DC] rounded-xl text-sm text-[#1C1917] placeholder-[#C4B9AD] resize-none outline-none focus:border-[#C17B3F] focus:ring-2 focus:ring-[#C17B3F]/15 transition-all mb-4"
      />
      {respondError && (
        <p className="text-sm text-red-600 mb-3">{respondError}</p>
      )}
      <div className="flex gap-3">
        <button
          onClick={() => onRespond("approve")}
          disabled={responding}
          className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-60"
        >
          {responding ? <Loader2 className="w-4 h-4 animate-spin" /> : <>
            <CheckCircle className="w-4 h-4" /> Approve
          </>}
        </button>
        <button
          onClick={() => onRespond("reject")}
          disabled={responding}
          className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-60"
        >
          <XCircle className="w-4 h-4" /> Reject
        </button>
      </div>
    </div>
  );
}